import { useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import Button from '@splunk/react-ui/Button';
import ControlGroup from '@splunk/react-ui/ControlGroup';
import Modal from '@splunk/react-ui/Modal';
import { StringLengthIndicator } from '@splunk/olly-common/StringLengthIndicator';
import Text from '@splunk/react-ui/Text';
import TextArea from '@splunk/react-ui/TextArea';

export type DetectorEditModalProps = {
    name?: string;
    description?: string;
    open?: boolean;
    handleRequestClose?: () => void;
    update: (params: { name?: string; description?: string }) => void;
};

const TITLE_LENGTH_LIMIT = 512;
const DESCRIPTION_LENGTH_LIMIT = 1024;

const StyledModal = styled(Modal)`
    width: 38.25rem;
`;

const StyledModalBody = styled(Modal.Body)`
    padding: 1.5rem;
`;

const StyledStringLengthIndicator = styled(StringLengthIndicator)`
    font-size: 0.75rem;
`;

export const DetectorEditModal = ({
    name: rawName,
    description: rawDescription,
    open,
    handleRequestClose,
    update,
}: DetectorEditModalProps): JSX.Element => {
    const [name, setName] = useState(rawName);
    const [description, setDescription] = useState(rawDescription);
    const modalToggle = useRef(null);

    useEffect(() => {
        setDescription(rawDescription);
    }, [rawDescription]);

    useEffect(() => {
        setName(rawName);
    }, [rawName]);

    return (
        <StyledModal
            open={open}
            initialFocus="container"
            onRequestClose={handleRequestClose}
            returnFocus={modalToggle}
        >
            <Modal.Header title="Edit name and description" />
            <StyledModalBody>
                <ControlGroup
                    help={
                        <StyledStringLengthIndicator
                            inputString={name || ''}
                            lengthLimit={TITLE_LENGTH_LIMIT}
                        />
                    }
                    label="Name"
                    labelPosition="top"
                >
                    <Text
                        maxLength={TITLE_LENGTH_LIMIT}
                        name="title"
                        onChange={(_, { value }): void => setName(value)}
                        value={name}
                    />
                </ControlGroup>

                <ControlGroup
                    help={
                        <StyledStringLengthIndicator
                            inputString={description || ''}
                            lengthLimit={DESCRIPTION_LENGTH_LIMIT}
                        />
                    }
                    label="Description"
                    labelPosition="top"
                >
                    <TextArea
                        maxLength={DESCRIPTION_LENGTH_LIMIT}
                        name="description"
                        onChange={(_, { value }): void => setDescription(value)}
                        rowsMin={6}
                        value={description}
                    />
                </ControlGroup>
            </StyledModalBody>
            <Modal.Footer>
                <Button appearance="secondary" onClick={handleRequestClose} label="Cancel" />
                <Button
                    onClick={(): void => {
                        update({ description, name });
                    }}
                    appearance="primary"
                    label="Update"
                    type="button"
                    ref={modalToggle}
                />
            </Modal.Footer>
        </StyledModal>
    );
};
